/**
 * What a chart shows: its figures, and how they are drawn.
 *
 * Shared by the chart section, which flows with the text, and by a chart
 * placed freely on a designed page (see `free.ts`), so a chart is the same
 * thing wherever it stands and edits the same way.
 *
 * The figures belong to the document, never to a live source: a report
 * exported today has to draw the same chart next year, when whatever produced
 * the figures is gone and the numbers themselves are the only record.
 *
 * Pure. Reading what was stored, the colours a chart is drawn in, and how a
 * figure is printed on it; nothing drawn here.
 */

import { readArray, readBoolean, readString, type JsonValue } from '@/domain/json'
import type { Layout } from '@/domain/layout'
import { isAccent } from '@/domain/layout/colour'

export type ChartType = 'bar' | 'line' | 'pie' | 'doughnut'
export type ChartOrientation = 'vertical' | 'horizontal'
/** Where the legend stands: `auto` beside a pie, under anything else. */
export type LegendPosition = 'auto' | 'top' | 'right' | 'bottom' | 'left'

export const CHART_TYPES: readonly ChartType[] = ['bar', 'line', 'pie', 'doughnut']
export const CHART_ORIENTATIONS: readonly ChartOrientation[] = ['vertical', 'horizontal']
export const LEGEND_POSITIONS: readonly LegendPosition[] = [
  'auto',
  'top',
  'right',
  'bottom',
  'left',
]

/** One series of figures in a chart. */
export interface ChartSeries {
  readonly id: string
  readonly label: string
  /** One value per label. Short rows are read as gaps, not as zeros. */
  readonly values: readonly number[]
  /** `#rrggbb`, or empty for a shade of the document's accent. */
  readonly colour: string
}

export interface ChartContent {
  readonly chartType: ChartType
  /** What the category axis reads, or what each slice is called. */
  readonly labels: readonly string[]
  readonly series: readonly ChartSeries[]
  /** Whether the series are named beside the chart. */
  readonly showLegend: boolean
  readonly legendPosition: LegendPosition
  /** Bars standing up or lying across. Only bars have the choice. */
  readonly orientation: ChartOrientation
  /** Series piled on one another rather than set side by side. Bars only. */
  readonly stacked: boolean
  /** Every figure printed on its bar, point or slice, as most printed reports do. */
  readonly showValues: boolean
  /** What every figure is in, printed after it: `%`, `GWh`, `M€`. Empty for none. */
  readonly unit: string
  /** The value scale and its grid. Off, the printed figures carry the values. */
  readonly showAxis: boolean
  /**
   * A colour per label, `#rrggbb` or empty for the palette: each slice of a
   * pie its own, or each bar of a one-series chart (one mode of transport
   * apart from the others, say). Ignored with several series, which are told
   * apart by their own colours.
   */
  readonly labelColours: readonly string[]
  /** Words set in the hole of a doughnut: the total, usually. */
  readonly centre: string
}

/** What a new chart starts as, before anybody has typed a figure. */
export function newChartContent(series: ChartSeries): ChartContent {
  return {
    chartType: 'bar',
    labels: ['2024', '2025', '2026'],
    series: [series],
    showLegend: true,
    legendPosition: 'auto',
    orientation: 'vertical',
    stacked: false,
    showValues: false,
    unit: '',
    showAxis: true,
    labelColours: [],
    centre: '',
  }
}

// ---------------------------------------------------------------------------
// Reading what was stored
// ---------------------------------------------------------------------------

/** A colour as `#rrggbb`, lower-cased, or empty for anything that is not one. */
function readColour(value: JsonValue | undefined): string {
  return typeof value === 'string' && isAccent(value) ? value.toLowerCase() : ''
}

function readSeries(value: JsonValue, newId: () => string): ChartSeries {
  return {
    id: readString(value, 'id') || newId(),
    label: readString(value, 'label'),
    // Anything unreadable becomes zero rather than dropping the point, which
    // would silently shift every value after it onto the wrong label.
    values: readArray(value, 'values').map((entry) =>
      typeof entry === 'number' && Number.isFinite(entry) ? entry : Number(entry) || 0,
    ),
    colour: readColour(readString(value, 'colour')),
  }
}

function oneOf<T extends string>(options: readonly T[], value: string, fallback: T): T {
  return (options as readonly string[]).includes(value) ? (value as T) : fallback
}

/**
 * A chart read back off a stored document, field by field: whatever was
 * stored by an earlier version, an import or a hand. A chart stored before a
 * field existed reads as it was drawn then: upright bars, side by side, the
 * scale shown and no figure printed.
 *
 * The id generator is handed in, so this module stays pure.
 */
export function readChartContent(stored: JsonValue | undefined, newId: () => string): ChartContent {
  return {
    chartType: oneOf(CHART_TYPES, readString(stored, 'chartType'), 'bar'),
    // Only strings are labels. Anything else becomes blank rather than being
    // stringified: an object rendered as "[object Object]" under a bar is
    // worse than a bar with no label at all.
    labels: readArray(stored, 'labels').map((entry) => (typeof entry === 'string' ? entry : '')),
    series: readArray(stored, 'series').map((entry) => readSeries(entry, newId)),
    showLegend: readBoolean(stored, 'showLegend', true),
    legendPosition: oneOf(LEGEND_POSITIONS, readString(stored, 'legendPosition'), 'auto'),
    orientation: oneOf(CHART_ORIENTATIONS, readString(stored, 'orientation'), 'vertical'),
    stacked: readBoolean(stored, 'stacked'),
    showValues: readBoolean(stored, 'showValues'),
    unit: readString(stored, 'unit').trim().slice(0, 12),
    showAxis: readBoolean(stored, 'showAxis', true),
    labelColours: readArray(stored, 'labelColours').map(readColour),
    centre: readString(stored, 'centre').slice(0, 60),
  }
}

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

/** Lightness steps, from the accent itself towards the paper. */
const SHADES = [1, 0.72, 0.5, 0.34, 0.22, 0.14]

/** An accent lightened towards the paper by a given amount. */
function mix(hex: string, weight: number): string {
  const value = Number.parseInt(hex.slice(1), 16)
  const towards = (channel: number): number => Math.round(channel * weight + 255 * (1 - weight))
  const [r, g, b] = [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff].map(towards)
  return `#${[r, g, b].map((channel) => (channel ?? 0).toString(16).padStart(2, '0')).join('')}`
}

/**
 * Colours built from the document's own accent.
 *
 * Told apart by lightness rather than by hue, so a report keeps one colour
 * and still reads in black and white, which is how a great many are printed.
 * Where the charter asks for its own colours (`house`), those come after the
 * accent, in their order, and the shades only once they run out.
 */
export function chartPalette(accent: string, house: readonly string[] = []): string[] {
  const base = isAccent(accent) ? accent : '#1c3f6e'
  const shades = SHADES.map((weight) => mix(base, weight))
  const own = house.filter(isAccent)
  return own.length === 0 ? shades : [base, ...own, ...shades.slice(1)]
}

/** The charter's colours a document's charts take, if it asks them to take any. */
export function chartHouse(layout: Pick<Layout, 'chartColours' | 'palette'>): readonly string[] {
  return layout.chartColours === 'palette' ? layout.palette : []
}

function shade(palette: readonly string[], at: number): string {
  return palette[at % palette.length] ?? palette[0] ?? '#1c3f6e'
}

/** Whether the chart's labels each wear a colour of their own rather than their series'. */
export function coloursByLabel(chart: ChartContent): boolean {
  if (chart.chartType === 'pie' || chart.chartType === 'doughnut') return true
  return chart.series.length === 1 && chart.labelColours.some(Boolean)
}

/** A series' colour: its own, or a shade of the accent by its place. */
export function seriesColour(chart: ChartContent, at: number, palette: readonly string[]): string {
  return chart.series[at]?.colour || shade(palette, at)
}

/** A label's colour: its own, or a shade of the accent by its place. */
export function labelColour(chart: ChartContent, at: number, palette: readonly string[]): string {
  return chart.labelColours[at] || shade(palette, at)
}

/** Ink that reads on a colour: the paper's on a dark one, the ink's on a light one. */
export function inkOn(colour: string): string {
  if (!isAccent(colour)) return '#16202c'
  const value = Number.parseInt(colour.slice(1), 16)
  const [r, g, b] = [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff]
  // Relative luminance, near enough: the eye weighs green most and blue least.
  return 0.299 * r + 0.587 * g + 0.114 * b < 150 ? '#ffffff' : '#16202c'
}

// ---------------------------------------------------------------------------
// Figures
// ---------------------------------------------------------------------------

/**
 * Figures are printed the French way, as the report's other figures are (see
 * the GHG and taxonomy blocks): a comma for the decimals, a thin space
 * between thousands.
 */
const FIGURES = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 })

/** A figure as it is printed on a chart, its unit after it the French way: `4 189 GWh`, `47 %`. */
export function printedFigure(value: number, unit: string): string {
  const number = FIGURES.format(value)
  return unit ? `${number}\u202f${unit}` : number
}

/** What was typed into a cell as a figure; zero for anything that is not one. */
export function figureOf(typed: string): number {
  const parsed = Number(typed.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}
